import Cookies from "js-cookie";
import { isValidPhoneNumber } from "libphonenumber-js";
import { DEFAULT_TRANSLATION } from "../constants";
import pluralize from "pluralize";

const HANDLED_ORDER_PICKUP = ["SKIPPED", "PICKUP_FAILED", "COLLECTED", "SKIPPED"];
const HANDLED_ORDER_DELIVERY = ["DELIVERY_FAILED", "COMPLETED", "SKIPPED"];
const HANDLED_ORDER_SKIP = ["SKIPPED"];

export const getTranslation = (setting, position, label) => {
  const translation = setting?.translation?.[setting?.systemLanguage];
  return translation?.[position]?.[label] || DEFAULT_TRANSLATION[position][label] || label;
};

export const getStopsOrder = (order, color = "606060") => {
  const stops = [];
  if (order.routePlanType === "PDP") {
    stops.push(getStopPickupOrder(order, color));
  }
  stops.push(getStopDeliveryOrder(order, color));
  return stops;
};

export const splitFirstOccurrence = (str, separator) => {
  const [first, ...rest] = str.split(separator);

  const remainder = rest.join(separator);

  return [first, remainder];
};

export const getProgressStatus = status => {
  let index = -1;
  if (status === "ASSIGNED") index = 0;
  if (status === "SKIPPED") index = 1;
  if (status === "PICKUP_ARRIVED") index = 1;
  if (status === "PICKUP_FAILED") index = 1;
  if (status === "COLLECTED") index = 1;
  if (status === "IN_TRANSIT") index = 2;
  if (status === "DELIVERY_ARRIVED") index = 3;
  if (status === "DELIVERY_FAILED") index = 3;
  if (status === "COMPLETED") index = 3;
  return index;
};

export const getStopPickupOrder = (order, color = "606060") => {
  let status = order.pickupStatus === "COLLECTED" ? "COMPLETED" : order.pickupStatus;
  if (order.status === "SKIPPED" && !["COLLECTED", "PICKUP_ARRIVED", "PICKUP_FAILED"].includes(order.pickupStatus))
    status = order.status;
  const stop = {
    id: `${order._id}-pickup`,
    orderId: order._id,
    orderCode: order.orderCode,
    orderRef: order.orderRef,
    name: order.pickupName,
    phone: order.pickupPhone,
    address: order.pickupAddress,
    lat: order.pickupLat,
    lng: order.pickupLng,
    status,
    type: "pickup",
    startTime: order.timeStart,
    endTime: order.timeEnd,
    color: order.driver?.color || color,
    driverName: order.driver?.name,
    driverId: order.driver?._id,
    notes: order.pickupNotes,
    driverNotes: order.driverNotes,
    pods: (order.pods ?? []).map(pod => order.podUrl + "/" + pod).join(","),
    customFields: order.fields,
    completedAt: order.logs.find(l => HANDLED_ORDER_PICKUP.includes(l.status))?.createdAt,
    skipNotes: order.logs.find(l => HANDLED_ORDER_SKIP.includes(l.status))?.notes,
    routePlanType: order.routePlanType,
    load: order.load,
    labels: order.labels,
  };

  return stop;
};

export const getStopDeliveryOrder = (order, color = "606060") => {
  const driverNotes = (order.logs || [])
    .map(l => l.notes)
    .filter(note => note && note.trim() !== "")
    .join("; ");

  const stop = {
    id: `${order._id}-delivery`,
    orderId: order._id,
    orderCode: order.orderCode,
    orderRef: order.orderRef,
    name: order.deliveryName,
    phone: order.deliveryPhone,
    address: order.deliveryAddress,
    lat: order.deliveryLat,
    lng: order.deliveryLng,
    status: order.status,
    type: "delivery",
    startTime: order.deliveryTimeStart,
    endTime: order.deliveryTimeEnd,
    color: order.driver?.color || color,
    driverName: order.driver?.name,
    driverId: order.driver?._id,
    notes: order.deliveryNotes,
    driverNotes,
    pods: (order.pods ?? []).map(pod => order.podUrl + "/" + pod).join(","),
    customFields: order.fields,
    completedAt: order.logs.find(l => HANDLED_ORDER_DELIVERY.includes(l.status))?.createdAt,
    skipNotes: order.logs.find(l => HANDLED_ORDER_SKIP.includes(l.status))?.notes,
    routePlanType: order.routePlanType,
    load: order.load,
    labels: order.labels,
  };
  return stop;
};

export const humanizeText = text => {
  return text?.replace(/[_]/g, " ").toLowerCase();
};

export const companyDeactivated = status => {
  return ["DISABLED", "BANNED"].includes(status);
};

export const pluralizeText = (word, count) => {
  if (!count) {
    return "-";
  }
  return pluralize(word, count, true);
};

export const setCountryCookie = (value, country, user) => {
  if (value && isValidPhoneNumber(`+${value}`, country.countryCode)) {
    Cookies.set(`CountryCode-${user._id}`, country.countryCode);
  }
};

export function convertMinutesToTime(minutes) {
  if (!minutes || minutes === "-") return "-";
  const totalSeconds = Math.floor(minutes * 60);
  const hours = Math.floor(totalSeconds / 3600);
  const remainingSeconds = totalSeconds % 3600;
  const mins = Math.floor(remainingSeconds / 60);
  const secs = remainingSeconds % 60;

  // Format with leading zeros if needed
  const formattedTime = [
    String(hours).padStart(2, "0"),
    String(mins).padStart(2, "0"),
    String(secs).padStart(2, "0"),
  ].join(":");

  return formattedTime;
}
